/**
 * SOLID Review
 * S — Strong. One concern: the game screen's ad config — whether to ask for this player's, and keeping
 *     the answer. (Which zone a session shows is `domain/ads/adZone.ts`, read when the view is built.)
 * O — Good. A new provider needs no change here.
 * L — N/A — functions.
 * I — Good. Two decisions on the one ClientDecisions contract.
 * D — Strong. The session draft and the port schemas.
 */
import { decided, type IEntityDecision, type TDecisionResult } from '../shared';
import { draftOf, settleSession } from '../session/sessionDraft';
import type { IPropsClientChanges, IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { IEntityTableAck } from '../../ports/schemas/TableEvents';
import type { ICommandNothing } from '../../ports/schemas/TableMoves';
import { NO_ADS, type IEntityAdConfigPlan, type ICommandAdConfigLoaded } from '../../ports/schemas/AdsMoves';

type TSession<D> = TDecisionResult<IEntityDecision<IPropsClientChanges, D>>;

const NOTHING: IPropsClientChanges = {};
const APPLIED: IEntityTableAck = { applied: true };
const NOT_APPLIED: IEntityTableAck = { applied: false };
const NO_REQUEST: IEntityAdConfigPlan = { request: null };

/**
 * Whether to ask for the seated player's ad config (frontend strict-ports plan, FE4d — useAdProviderConfig,
 * moved): a free player, seated, not yet asked for. Asking is marked BEFORE the call, as the hook's ref
 * was, so a failed request is not repeated for the same player; a new player starts from the defaults.
 */
export function decidePlanAdConfig(input: IPropsClientInput, _command: ICommandNothing): TSession<IEntityAdConfigPlan> {
  const { player, ads } = input.session;
  if (player === null || player.matchCredits > 0 || ads.playerId === player.playerId) return decided(NOTHING, NO_REQUEST);
  const next = draftOf(input);
  next.session.ads = { ...NO_ADS, playerId: player.playerId };
  const plan: IEntityAdConfigPlan = { request: { playerId: player.playerId } };
  return decided(settleSession(input, next), plan);
}

/** The server's answer for a player: kept if it is still the player asked for; none keeps the defaults. */
export function decideAdConfigLoaded(input: IPropsClientInput, command: ICommandAdConfigLoaded): TSession<IEntityTableAck> {
  if (command.config === null || input.session.ads.playerId !== command.playerId) return decided(NOTHING, NOT_APPLIED);
  const next = draftOf(input);
  next.session.ads = { ...command.config, playerId: command.playerId };
  return decided(settleSession(input, next), APPLIED);
}
